// Leituras do histórico de push direto do Firestore (regras: só admin lê).
// Escrita de campanha é sempre pela API (services/pushApi.ts).
import {
  collection,
  doc,
  DocumentData,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { PushCampaign, PushRecipient } from '../types/push';

export const CAMPAIGNS_PAGE_SIZE = 20;

const toDate = (value: unknown): Date | null => (value instanceof Timestamp ? value.toDate() : null);

const toCampaign = (id: string, data: DocumentData): PushCampaign => ({
  id,
  name: data.name || data.title || '',
  title: data.title || '',
  body: data.body || '',
  imageUrl: data.imageUrl || null,
  screen: data.screen || null,
  customData: data.customData || {},
  sound: data.sound ?? true,
  androidChannelId: data.androidChannelId || '',
  ttlSeconds: data.ttlSeconds || 0,
  audience: data.audience,
  audienceLabel: data.audienceLabel || '',
  isTest: !!data.isTest,
  status: data.status,
  createdAt: toDate(data.createdAt),
  scheduledAt: toDate(data.scheduledAt),
  sendingStartedAt: toDate(data.sendingStartedAt),
  sentAt: toDate(data.sentAt),
  createdBy: data.createdBy || null,
  stats: data.stats || null,
  metricsFrozenAt: toDate(data.metricsFrozenAt),
  warnings: data.warnings || [],
  error: data.error || null,
});

const toRecipient = (id: string, data: DocumentData): PushRecipient => ({
  id,
  userId: data.userId,
  businessName: data.businessName || 'Sem nome',
  deviceLabel: data.deviceLabel || 'Dispositivo',
  platform: data.platform || 'unknown',
  tokenSuffix: data.tokenSuffix || '',
  permissionAtSend: data.permissionAtSend || null,
  appVersionAtSend: data.appVersionAtSend || null,
  status: data.status,
  errorCode: data.errorCode || null,
  sentAt: toDate(data.sentAt),
  receivedAt: toDate(data.receivedAt),
  openedAt: toDate(data.openedAt),
});

export interface CampaignsPage {
  campaigns: PushCampaign[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/** Campanhas da mais nova para a mais antiga, CAMPAIGNS_PAGE_SIZE por vez. */
export const fetchCampaignsPage = async (after?: QueryDocumentSnapshot<DocumentData> | null): Promise<CampaignsPage> => {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (after) constraints.push(startAfter(after));
  constraints.push(limit(CAMPAIGNS_PAGE_SIZE));

  const snapshot = await getDocs(query(collection(db, 'pushCampaigns'), ...constraints));
  return {
    campaigns: snapshot.docs.map(snap => toCampaign(snap.id, snap.data())),
    cursor: snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length === CAMPAIGNS_PAGE_SIZE,
  };
};

/** Quantos destinatários abriram (agregação no servidor: 1 leitura a cada 1000). */
export const countOpened = async (campaignId: string): Promise<number> => {
  const recipients = collection(db, 'pushCampaigns', campaignId, 'recipients');
  const snapshot = await getCountFromServer(query(recipients, where('openedAt', '!=', null)));
  return snapshot.data().count;
};

export const subscribeCampaign = (
  campaignId: string,
  onChange: (campaign: PushCampaign | null) => void,
  onError: (error: Error) => void
) =>
  onSnapshot(
    doc(db, 'pushCampaigns', campaignId),
    snap => onChange(snap.exists() ? toCampaign(snap.id, snap.data()) : null),
    onError
  );

export const subscribeRecipients = (
  campaignId: string,
  onChange: (recipients: PushRecipient[]) => void,
  onError: (error: Error) => void
) =>
  onSnapshot(
    collection(db, 'pushCampaigns', campaignId, 'recipients'),
    snapshot => onChange(snapshot.docs.map(snap => toRecipient(snap.id, snap.data()))),
    onError
  );
